import { useState } from 'react'
import { dateBounds, isSaneDate } from '../lib/format'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { createEvent, getOwnerEvent, qrPngUrl, updateEvent } from '../api/owner'
import type { EventVisibility, OwnerEvent } from '../api/ownerTypes'
import { LandingPhotos } from './components/LandingPhotos'
import { AccentChoice, VisibilityChoice } from './components/EventSettingsFields'
import { QrCode } from './components/QrCode'
import { readable } from './format'

type Step = 1 | 2 | 3 | 4

/**
 * Ronde 20J tot en met 20M — een nieuw event in drie stappen, plus het scherm
 * dat erna komt.
 *
 * Het event wordt aan het einde van stap 1 al aangemaakt. Dat moet ook: stap 2
 * uploadt foto's naar de bucket en die moeten ergens bij horen. Wie daarna
 * afhaakt houdt een concept in "Mijn events" — precies de tweede kaart uit
 * ronde 20A.
 *
 * Op desktop staat dezelfde wizard als dialoog over "Mijn events" (ronde 20V);
 * dat is puur een andere omlijsting om dezelfde stappen.
 */
export function NewEventPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const [step, setStep] = useState<Step>(1)
  const [eventId, setEventId] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [title, setTitle] = useState('')
  const [date, setDate] = useState('')
  const [coupleNames, setCoupleNames] = useState('')
  const [venue, setVenue] = useState('')
  const [visibility, setVisibility] = useState<EventVisibility>('Shared')
  const [accent, setAccent] = useState('#A0552F')

  const subject = useQuery({
    queryKey: ['owner', 'event', eventId],
    queryFn: () => getOwnerEvent(eventId!),
    enabled: eventId !== null,
  })

  async function run(work: () => Promise<void>) {
    setBusy(true)
    setError(null)

    try {
      await work()
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Er ging iets mis.')
    } finally {
      setBusy(false)
    }
  }

  const toStepTwo = () =>
    run(async () => {
      const created = await createEvent(title.trim(), date ? new Date(date).toISOString() : null)

      setEventId(created.eventId)

      // De namen die gasten zien beginnen gelijk aan de eventnaam; stap 2 mag
      // ze overschrijven.
      setCoupleNames(title.trim())
      setStep(2)
    })

  const toStepThree = () =>
    run(async () => {
      await updateEvent(eventId!, { coupleNames: coupleNames.trim(), venue: venue.trim() })
      await subject.refetch()
      setStep(3)
    })

  const finish = () =>
    run(async () => {
      // Geen publiceerstap meer: het event is bruikbaar zodra het bestaat. Wie
      // het aanmaakt heeft als enige de code, dus er valt niets af te schermen
      // tot hij hem deelt — en deelt hij hem, dan is dat juist de bedoeling.
      await updateEvent(eventId!, { visibility, accentColor: accent })
      await subject.refetch()
      await queryClient.invalidateQueries({ queryKey: ['owner', 'events'] })
      setStep(4)
    })

  const close = () => navigate('/beheer')

  return (
    <div className="flex min-h-full flex-col lg:mx-auto lg:min-h-0 lg:w-[680px] lg:py-12">
      <div className="lg:rounded-dialog lg:bg-shell lg:shadow-dialog flex flex-1 flex-col lg:px-9 lg:py-8">
        {step < 4 && (
          <>
            <header className="flex items-center justify-between px-5.5 pt-5.5 lg:px-0 lg:pt-0">
              <button
                type="button"
                aria-label={step === 1 ? 'Sluiten' : 'Terug'}
                onClick={() => (step === 1 ? close() : setStep((step - 1) as Step))}
                className="border-line text-ink-muted hover:bg-tint/50 hover:text-ink flex size-9.5 items-center justify-center rounded-full border text-[15px] transition-colors lg:order-2 lg:size-8.5"
              >
                {step === 1 ? '✕' : '←'}
              </button>

              <p className="text-ink-faint text-[13px] lg:order-1">Stap {step} van 3</p>

              <span className="w-9.5 lg:hidden" />
            </header>

            <div className="flex gap-1.25 px-5.5 pt-4.5 lg:px-0 lg:pt-4">
              {[1, 2, 3].map((index) => (
                <span
                  key={index}
                  className={
                    'h-0.75 flex-1 rounded-full ' + (index <= step ? 'bg-accent' : 'bg-line-card')
                  }
                />
              ))}
            </div>
          </>
        )}

        {step === 1 && (
          <StepOne
            title={title}
            onTitle={setTitle}
            date={date}
            onDate={setDate}
            busy={busy}
            onNext={toStepTwo}
          />
        )}

        {step === 2 && subject.data && (
          <StepTwo
            subject={subject.data}
            coupleNames={coupleNames}
            onCoupleNames={setCoupleNames}
            venue={venue}
            onVenue={setVenue}
            busy={busy}
            onBack={() => setStep(1)}
            onNext={toStepThree}
            onPhotosChanged={() => subject.refetch()}
          />
        )}

        {step === 3 && (
          <StepThree
            visibility={visibility}
            onVisibility={setVisibility}
            accent={accent}
            onAccent={setAccent}
            busy={busy}
            onBack={() => setStep(2)}
            onFinish={finish}
          />
        )}

        {step === 4 && subject.data && <Done subject={subject.data} />}

        {error && (
          <p role="alert" className="text-accent px-5.5 pb-4 text-sm lg:px-0">
            {error}
          </p>
        )}
      </div>
    </div>
  )
}

/** Ronde 20J — naam en datum. */
function StepOne({
  title,
  onTitle,
  date,
  onDate,
  busy,
  onNext,
}: {
  title: string
  onTitle: (value: string) => void
  date: string
  onDate: (value: string) => void
  busy: boolean
  onNext: () => void
}) {
  return (
    <>
      <div className="px-5.5 pt-8 lg:px-0 lg:pt-6.5">
        <h1 className="font-display text-[38px] leading-[1.1] lg:text-[36px]">
          Wat vieren jullie?
        </h1>
        <p className="text-ink-soft mt-2.5 text-[15px] leading-[1.55] text-pretty">
          Twee dingen zijn genoeg om te starten. De rest kan later.
        </p>
      </div>

      <div className="flex flex-col gap-6 px-5.5 pt-7.5 lg:px-0 lg:pt-6.5">
        <div>
          <label
            htmlFor="event-title"
            className="text-ink-faint mb-2.5 block text-[11px] tracking-[0.18em] uppercase"
          >
            Naam van het event
          </label>
          <input
            id="event-title"
            value={title}
            onChange={(event) => onTitle(event.target.value)}
            maxLength={120}
            placeholder="Onze trouw"
            className={
              'font-display text-ink caret-accent w-full border-b bg-transparent pb-3 text-[28px] ' +
              'placeholder:text-ink-ghost outline-none ' +
              (title.trim() ? 'border-accent' : 'border-line')
            }
          />
          <p className="text-ink-faint mt-2 text-[13px]">
            Alleen voor jullie, gasten zien dit niet.
          </p>
        </div>

        <div>
          <label
            htmlFor="event-date"
            className="text-ink-faint mb-2.5 block text-[11px] tracking-[0.18em] uppercase"
          >
            Datum
          </label>
          <input
            id="event-date"
            type="date"
            value={date}
            min={dateBounds.min}
            max={dateBounds.max}
            onChange={(event) => {
              // Een jaartal van zes cijfers houden we hier al tegen, zodat het
              // niet in de state belandt en straks als datum wordt opgeslagen.
              if (isSaneDate(event.target.value)) {
                onDate(event.target.value)
              }
            }}
            className="rounded-row border-line-card bg-surface focus:border-accent w-full border px-4 py-3.75 text-base outline-none"
          />
          <p className="text-ink-faint mt-2 text-[13px]">Weet je die nog niet? Sla over.</p>
        </div>
      </div>

      <div className="flex-1" />

      <div className="px-5.5 pt-6 pb-7.5 lg:px-0 lg:pb-0">
        <button
          type="button"
          onClick={onNext}
          disabled={!title.trim() || busy}
          className="bg-accent text-accent-ink shadow-accent-lg disabled:bg-inert disabled:text-inert-ink flex h-14.5 w-full items-center justify-center rounded-full text-[17px] font-semibold transition hover:brightness-95 disabled:shadow-none lg:ml-auto lg:h-13 lg:w-auto lg:px-8"
        >
          {busy ? 'Even geduld…' : 'Volgende'}
        </button>
      </div>
    </>
  )
}

/** Ronde 20K en 20V — wat gasten zien. */
function StepTwo({
  subject,
  coupleNames,
  onCoupleNames,
  venue,
  onVenue,
  busy,
  onBack,
  onNext,
  onPhotosChanged,
}: {
  subject: OwnerEvent
  coupleNames: string
  onCoupleNames: (value: string) => void
  venue: string
  onVenue: (value: string) => void
  busy: boolean
  onBack: () => void
  onNext: () => void
  onPhotosChanged: () => Promise<unknown>
}) {
  return (
    <>
      <div className="px-5.5 pt-8 lg:px-0 lg:pt-6.5">
        <h1 className="font-display text-[38px] leading-[1.1] lg:text-[36px]">
          Wat zien jullie gasten?
        </h1>
        <p className="text-ink-soft mt-2.5 text-[15px] leading-[1.55] text-pretty">
          Dit staat op de pagina die ze na het scannen krijgen.
        </p>
      </div>

      <div className="flex flex-col gap-5.5 px-5.5 pt-7 md:flex-row md:gap-7 lg:px-0 lg:pt-6.5">
        <div className="flex flex-1 flex-col gap-5">
          <div>
            <label
              htmlFor="couple-names"
              className="text-ink-faint mb-2.5 block text-[11px] tracking-[0.18em] uppercase lg:text-xs"
            >
              Namen
            </label>
            <input
              id="couple-names"
              value={coupleNames}
              onChange={(event) => onCoupleNames(event.target.value)}
              maxLength={200}
              className="font-display rounded-row border-line-card bg-surface focus:border-accent w-full border px-4 py-3.75 text-2xl outline-none"
            />
          </div>

          <div>
            <label
              htmlFor="venue"
              className="text-ink-faint mb-2.5 block text-[11px] tracking-[0.18em] uppercase lg:text-xs"
            >
              Locatie
            </label>
            <input
              id="venue"
              value={venue}
              onChange={(event) => onVenue(event.target.value)}
              maxLength={160}
              placeholder="Bijvoorbeeld Hoeve De Linde"
              className="rounded-row border-line-card bg-surface placeholder:text-ink-ghost focus:border-accent w-full border px-4 py-3.75 text-base outline-none"
            />
          </div>
        </div>

        <div className="flex-1">
          <LandingPhotos eventId={subject.id} photos={subject.photos} onChanged={onPhotosChanged} />
        </div>
      </div>

      <div className="flex-1" />

      <WizardFooter busy={busy} onBack={onBack} onNext={onNext} nextLabel="Volgende" />
    </>
  )
}

/** Ronde 20L — privacy en kleur. */
function StepThree({
  visibility,
  onVisibility,
  accent,
  onAccent,
  busy,
  onBack,
  onFinish,
}: {
  visibility: EventVisibility
  onVisibility: (value: EventVisibility) => void
  accent: string
  onAccent: (value: string) => void
  busy: boolean
  onBack: () => void
  onFinish: () => void
}) {
  return (
    <>
      <div className="px-5.5 pt-8 lg:px-0 lg:pt-6.5">
        <h1 className="font-display text-[38px] leading-[1.1] lg:text-[36px]">Nog twee keuzes</h1>
        <p className="text-ink-soft mt-2.5 text-[15px] leading-[1.55] text-pretty">
          Allebei achteraf nog aanpasbaar.
        </p>
      </div>

      <div className="px-5.5 pt-6.5 lg:px-0">
        <p className="text-ink-faint mb-3 text-[11px] tracking-[0.18em] uppercase lg:text-xs">
          Wie ziet welke foto’s
        </p>
        <VisibilityChoice value={visibility} onChange={onVisibility} />

        <p className="text-ink-faint mt-6 mb-3 text-[11px] tracking-[0.18em] uppercase lg:text-xs">
          Kleur van het event
        </p>
        <AccentChoice value={accent} onChange={onAccent} />
      </div>

      <div className="flex-1" />

      <WizardFooter busy={busy} onBack={onBack} onNext={onFinish} nextLabel="Event aanmaken" />
    </>
  )
}

function WizardFooter({
  busy,
  onBack,
  onNext,
  nextLabel,
}: {
  busy: boolean
  onBack: () => void
  onNext: () => void
  nextLabel: string
}) {
  return (
    <div className="flex gap-2.5 px-5.5 pt-4 pb-7.5 lg:justify-end lg:px-0 lg:pt-7.5 lg:pb-0">
      <button
        type="button"
        onClick={onBack}
        className="border-line text-ink-muted hover:bg-tint/50 hover:text-ink h-14.5 shrink-0 basis-25 rounded-full border text-base transition-colors lg:h-13 lg:basis-auto lg:px-6.5"
      >
        Terug
      </button>
      <button
        type="button"
        onClick={onNext}
        disabled={busy}
        className="bg-accent text-accent-ink shadow-accent-lg disabled:bg-inert disabled:text-inert-ink h-14.5 flex-1 rounded-full text-[17px] font-semibold transition hover:brightness-95 disabled:shadow-none lg:h-13 lg:flex-none lg:px-8 lg:text-base"
      >
        {busy ? 'Even geduld…' : nextLabel}
      </button>
    </div>
  )
}

/** Ronde 20M — klaar, met de QR meteen in beeld. */
function Done({ subject }: { subject: OwnerEvent }) {
  const navigate = useNavigate()

  return (
    <div className="flex flex-1 flex-col items-center px-5.5 text-center lg:px-0">
      <div className="flex-1" />

      <div className="px-3 lg:px-0">
        <span
          aria-hidden="true"
          className="bg-accent text-accent-ink mx-auto flex size-14 items-center justify-center rounded-full text-2xl transition hover:brightness-95"
        >
          ✓
        </span>

        <h1 className="font-display mt-5.5 text-[36px] leading-[1.1]">
          {subject.title} staat klaar
        </h1>
        <p className="text-ink-soft mt-2.5 text-[15px] leading-[1.55] text-pretty">
          Leg deze code op de tafels. Gasten scannen en uploaden, zonder account.
        </p>

        <div className="mt-6.5 flex justify-center">
          <QrCode src={qrPngUrl(subject.id)} size={168} className="border-line-card border" />
        </div>

        <p className="text-ink-muted mt-4 font-mono text-[13px]">{readable(subject.displayUrl)}</p>
      </div>

      <div className="flex-1" />

      <div className="flex w-full flex-col gap-2.5 pt-6 pb-7.5 lg:max-w-[420px] lg:pb-0">
        <a
          href={qrPngUrl(subject.id)}
          download
          className="bg-accent text-accent-ink shadow-accent-lg flex h-14.5 items-center justify-center rounded-full text-[17px] font-semibold transition hover:brightness-95"
        >
          QR downloaden
        </a>
        <button
          type="button"
          onClick={() => navigate(`/beheer/e/${subject.id}`)}
          className="border-line text-ink-muted hover:bg-tint/50 hover:text-ink h-13.5 rounded-full border text-base transition-colors"
        >
          Naar het event
        </button>
      </div>
    </div>
  )
}
